import {
  convertToInternationalCurrencySystem,
  getAvatar,
  getDateTime,
} from 'utils';
import translation from 'translation';
import firstPlaceBadge from 'assets/img/brandbaan/first-place-badge.svg';
import secondPlaceBadge from 'assets/img/brandbaan/second-place-badge.svg';
import thirdPlaceBadge from 'assets/img/brandbaan/third-place-badge.svg';

import {
  distributionChartHelper,
  infoBoxHelper,
  platformsDataHelper,
  trendChartHelper,
  normalizeVoiceTrendChartData,
  sentimentHelper,
  calculateEngagement,
  calculateWeeks,
  formatUnlessZero,
  calculateWeeklyPublishingRate,
  adaptTips,
  adaptChanges,
  adaptChangeClass,
  pieChartDataHelper,
  getBrandQuery,
} from './brandBaan.helpers';

const { t } = translation;

export const profilesAdapter = (data, params) => {
  const badges = [firstPlaceBadge, secondPlaceBadge, thirdPlaceBadge];
  const weeksCount = calculateWeeks(params);
  const sortedData = data?.sort((a, b) => b.follower_count - a.follower_count);
  return sortedData?.map((item, index) => ({
    id: item.id,
    avatar: getAvatar(item?.avatar, params.platform),
    name: item.title || t(`common:${params.platform}NotDetermined`),
    username: item.username,
    owner: item.tag,
    platform: params.platform,
    badge: sortedData.length > 1 && badges[index],
    info: [
      {
        icon: 'ri-user-3-line',
        title:
          params.platform === 'telegram'
            ? t('brandBaan:profiles.subscribersCount')
            : t('brandBaan:profiles.followerCount'),
        metricValue: convertToInternationalCurrencySystem(item.follower_count),
        change: formatUnlessZero(item.follower_count_diff),
      },
      params.platform !== 'telegram' && {
        icon: 'ri-flashlight-line',
        title: t('brandBaan:profiles.engagementRate'),
        metricValue: `${calculateEngagement(item.engagement_rate)}%`,
        change: formatUnlessZero(item.engagement_rate_diff),
      },
      {
        icon: 'ri-file-list-3-line',
        title: t('brandBaan:profiles.weeklyPublishRate'),
        metricValue:
          weeksCount === 0
            ? calculateWeeklyPublishingRate(item.post_count)
            : calculateWeeklyPublishingRate(item.post_count / weeksCount),
        change: formatUnlessZero(item.post_count_diff),
      },
    ].filter(Boolean),
  }));
};

export const voiceShareAdapter = (data, brandQuerires) => {
  const chartData =
    data?.voice_of_shares && data?.voice_of_shares?.length > 0
      ? data?.voice_of_shares
      : [];

  return {
    text: data?.tips || '',
    chart: pieChartDataHelper(chartData, brandQuerires),
  };
};

export const contentAdapter = (contentData, competitor) => {
  const { myBrandQuery, competitorQuery } = getBrandQuery(competitor?.id);
  const brandName = contentData.brands.my_brand;

  const resources = infoBoxHelper(
    contentData.posts_insights.my_brand_posts_count,
    contentData.posts_insights.competitor_posts_count,
    brandName,
    'contents'
  );

  const contents = infoBoxHelper(
    contentData.resources_insights.my_brand_resources_count,
    contentData.resources_insights.competitor_resources_count,
    brandName,
    'resources'
  );

  const sentiment = sentimentHelper(
    [
      {
        ...contentData.sentiment_chart.my_brand_sentiment,
        title: t('brandBaan:contents.sentimentStackedBarChart.myBrand'),
        isMyBrand: true,
        query: myBrandQuery,
      },
      {
        ...contentData.sentiment_chart.competitor_sentiment,
        title: t('brandBaan:contents.sentimentStackedBarChart.competitor'),
        isMyBrand: false,
        query: competitorQuery,
      },
    ],
    competitor?.name
  );

  const distribution = distributionChartHelper(
    contentData.posts_distribution_chart.my_brand_posts_distribution,
    contentData.posts_distribution_chart.competitor_posts_distribution,
    competitor?.name
  );

  const publishment = trendChartHelper({
    chartData: contentData.publishing_chart,
    chartDataKey: {
      myBrand: 'my_brand_publishing_chart_series',
      competitor: 'competitor_publishing_chart_series',
    },
    translationPerfix: 'contents',
    translationKey: 'publishingChartLegend',
    competitorName: competitor?.name,
  });

  const platformsData = platformsDataHelper(
    contentData.platform_posts.my_brand_platform_posts
  );

  return {
    resources,
    contents,
    sentiment,
    distribution,
    publishment,
    platformsData,
    tips: adaptTips(contentData?.tips),
  };
};

export const commentAdapter = (commnetData, competitor) => {
  const { myBrandQuery, competitorQuery } = getBrandQuery(competitor?.id);
  const brandName = commnetData.brands.my_brand;
  const comments = infoBoxHelper(
    commnetData.posts_insights.my_brand_posts_count,
    commnetData.posts_insights.competitor_posts_count,
    brandName,
    'comments'
  );
  const resources = infoBoxHelper(
    commnetData.resources_insights.my_brand_resources_count,
    commnetData.resources_insights.competitor_resources_count,
    brandName,
    'commentors'
  );
  const publishment = trendChartHelper({
    chartData: commnetData.publishing_chart,
    chartDataKey: {
      myBrand: 'my_brand_publishing_chart_series',
      competitor: 'competitor_publishing_chart_series',
    },
    translationPerfix: 'comments',
    translationKey: 'publishingChartLegend',
    competitorName: competitor?.name,
  });
  const sentiment = sentimentHelper(
    [
      {
        ...commnetData.sentiment_chart.my_brand_sentiment,
        title: t('brandBaan:contents.sentimentStackedBarChart.myBrand'),
        isMyBrand: true,
        query: myBrandQuery,
      },
      {
        ...commnetData.sentiment_chart.competitor_sentiment,
        title: t('brandBaan:contents.sentimentStackedBarChart.competitor'),
        isMyBrand: false,
        query: competitorQuery,
      },
    ],
    competitor?.name
  );
  return {
    comments,
    resources,
    publishment,
    sentiment,
    tips: adaptTips(commnetData?.tips),
  };
};

export const profileOverviewAdapter = (profileOverview) => {
  const insights = [
    {
      title: t('brandBaan:profileOverview.insight.totalFollowers'),
      count: convertToInternationalCurrencySystem(
        profileOverview.followers.total_followers
      ),
      change: '',
      changeClass: adaptChangeClass(profileOverview.followers.change),
      icon: 'ri-team-line',
      tooltip: t('brandBaan:profileOverview.insight.totalFollowers.tooltip'),
      sectionId: 'profiles',
    },
    {
      title: t('brandBaan:profileOverview.insight.totalEngagements'),
      count: convertToInternationalCurrencySystem(
        profileOverview.engagements.total_engagement
      ),
      change: `${
        profileOverview.engagements.change > 0 ? '+' : '-'
      }${convertToInternationalCurrencySystem(
        profileOverview.engagements.change
      )}`,
      changeClass: adaptChangeClass(profileOverview.engagements.change),
      icon: 'ri-hand-heart-line',
      tooltip: t('brandBaan:profileOverview.insight.totalEngagements.tooltip'),
      sectionId: 'profiles',
    },
    {
      title: t('brandBaan:profileOverview.insight.voiceShare'),
      count: adaptChanges(profileOverview.voice_of_shares.current, false),
      change: adaptChanges(profileOverview.voice_of_shares.change),
      changeClass: adaptChangeClass(profileOverview.voice_of_shares.change),
      icon: 'ri-megaphone-line',
      tooltip: t('brandBaan:profileOverview.insight.voiceShare.tooltip'),
      sectionId: 'voice-share',
    },
    {
      title: t('brandBaan:profileOverview.insight.totalContents'),
      count: convertToInternationalCurrencySystem(
        profileOverview.posts_insight.current
      ),
      change: adaptChanges(profileOverview.posts_insight.change),
      changeClass: adaptChangeClass(profileOverview.posts_insight.change),
      icon: 'ri-file-copy-line',
      tooltip: t('brandBaan:profileOverview.insight.totalContents.tooltip'),
      sectionId: 'contents',
    },
  ];
  return insights;
};

export const commentEmojiAdapter = (emojis) => {
  return {
    data: emojis?.data?.map((item) => ({
      text: item.emoji,
      value: item.count,
    })),
    meta: emojis?.meta,
  };
};

export const voiceTrendAdapter = (voiceTrendData, competitorName, platform) => {
  return trendChartHelper({
    chartData: normalizeVoiceTrendChartData(voiceTrendData, platform),
    chartDataKey: {
      myBrand: 'my_brand_series',
      competitor: 'competitor_series',
    },
    translationKey: 'voiceTrend.chartLegend',
    competitorName: competitorName,
  });
};

export const brandNssAdapter = (data) => {
  const score = data?.net_sentiment_score;
  if (!score) return null;

  return {
    myBrand: {
      nss: score.my_brand_nss,
      previousNss: score.my_brand_past_nss,
    },
    competitor: {
      nss: score.competitor_nss,
      previousNss: score.competitor_past_nss,
    },
  };
};

export const brandNssTrendAdapter = (trendData, competitorName, platform) => {
  return trendChartHelper({
    chartData: normalizeVoiceTrendChartData(trendData, platform),
    chartDataKey: {
      myBrand: 'my_brand_series',
      competitor: 'competitor_series',
    },
    translationKey: 'nss.chartLegend',
    competitorName: competitorName,
    valueKey: 'nss',
  });
};

export const storyPublicationComparisonAdapter = (data) => {
  const adapted = [];
  data.my_brand.forEach((myBrand, index) => {
    const { date } = getDateTime(myBrand.date);
    const item = {
      name: date,
      myBrand: myBrand.count,
      competitorBrand: data?.competitor_brand?.[index]?.count,
    };

    adapted.push(item);
  });
  return adapted;
};

export const storyPublicationChartAdapter = (data) => {
  const chartData = data.map((item) => ({
    ...item,
    title: item.title || item.brand_title,
    label: item.label === 'my_brand' ? 'my-brand' : item.label,
  }));

  return pieChartDataHelper(chartData);
};
